/**
 * Route guard utilities for capability-based navigation access control.
 *
 * Each route component is mapped to an access check that combines client-level
 * and user-level capability checks from the permissions layer. The main export
 * `hasAccess()` is called by the Vue Router `beforeEach` guard to allow or
 * deny navigation.
 */
import User from "@/data/models/User";
import { ProjectSettingsV2 } from "@/data/models/Settings";
import { getEffectiveUserLazily } from "@/utils/EffectiveUserLazy";
import { isViewAsSelected } from "@/store/modules/TargetUserModule";

import {
  defineOwnAllDevicesSettingsClientAccess,
  allDevicesSettingsClientAccess,
  defineProjectManagementSettingsForGroupClientAccess,
  manageAllDevicesClientAccess,
  manageHyDryDevicesClientAccess,
  manageHyDryMessagesSettingsClientAccess,
  manageMessagesClientAccess,
  manageProjectsClientAccess,
  manageTimeTrackingClientAccess,
  isOfficeAdminPortalClient,
} from "@/permissions/ClientPermissions";
import {
  alwaysAllowedToViewTimeTrackingAccess,
  companySettingsAccess,
  createMemberClientAccess,
  createProjectAccess,
  deviceSettingsItemAccess,
  mapPageAccess,
  miraAccess,
  hyDryDeviceSettingsAccess,
  importDevicesAccess,
  iresSettingsAccess,
  mailboxAccess,
  messageSelectionAccess,
  messageSettingsAccess,
  otherDeviceSettingsAccess,
  projectSettingsAccess,
  reportSettingsAccess,
  roleManagementAccess,
  schedulingSettingsAccess,
  systemConfigurationAccess,
  systemSettingsSectionAccess,
  timeTrackingSettingsAccess,
  userSettingsAccess,
  usersConfigurationAccess,
  vehicleManagementAccess,
  viewAdminsitrationSettingsAccess,
  viewSystemConfigurationDistributionAccess,
  billingCatalogSettingsAccess
} from "@/permissions/UserPermissions";
import { canAccessTimeTrackingHistory } from "@/utils/timeTrackingHistoryAccess";

export enum ComponentName {
  LOGIN = "Login",
  RESET_PASSWORD = "ResetPassword",
  SCAFFOLD = "IRES",
  ALL_MESSAGES = "AllMessages",
  MESSAGE_LIST = "MessageList",
  RATE_DRYING = "RateDrying",
  DRYING_HISTORY = "DryingHistory",
  IRES_DEVICE_DETAIL = "IresDeviceDetail",
  SAMPLES = "Samples",
  COCKPIT = "Cockpit",
  NOT_FOUND = "NotFound",
  HELP = "Help",
  MAP = "Map",
  MIRA = "Mira",
  MIRA_DATA_QUALITY = "MiraDataQuality",
  MIRA_DRYING = "MiraDrying",
  MIRA_DEVICES = "MiraDevices",
  MIRA_BUSINESS_DEVELOPMENT = "MiraBusinessDevelopment",
  MIRA_PROCESS_FLOW = "MiraProcessFlow",
  MIRA_INSURANCE = "MiraInsurance",
  MIRA_UNLOCK = "MiraUnlock",
  SCHEDULING = "Scheduling",
  VEHICLES = "Vehicles",
  VEHICLE_DEVICES = "VehicleDevices",
  TIME_TRACKING_HISTORY = "TimeTrackingHistory",
  SETTINGS = "Settings",
  USER_MANAGEMENT = "UserManagement",
  REPORT_SETTINGS = "ReportSettings",
  COMPANY_DATA = "CompanyData",
  COMPANY_SETTINGS = "CompanySettings",
  PROJECT_SETTINGS = "ProjectSettings",
  PROJECT_EDITING_SETTINGS = "ProjectEditingSettings",
  PROJECT_DEVICES_SETTINGS = "ProjectDevicesSettings",
  PROJECT_MANAGER_SETTINGS = "ProjectManagerSettings",
  DEVICE_MANAGER_SETTINGS = "DeviceManagerSettings",
  WORKING_HOURS = "WorkingHours",
  TIME_TRACKING_CONFIGURATIONS = "TimeTrackingConfigurations",
  DAILY_TRACKING_SETTINGS = "DailyTrackingSettings",
  SCHEDULING_SETTINGS = "SchedulingSettings",
  USER_SETTINGS = "UserSettings",
  VEHICLE_MANAGEMENT = "VehicleManagement",
  ROLE_MANAGEMENT = "RoleManagement",
  PERMISSION_TABLE = "PermissionTable",
  BRANCHES = "Branches",
  SYSTEM_CONFIGURATION = "SystemConfiguration",
  USER_SETTINGS_ADMINISTRATION = "UserSettingsAdministration",
  ROLE_SETTINGS_ADMINISTRATION = "RoleSettingsAdministration",
  SYSTEM_CONFIGURATION_ADMINISTRATION = "SystemConfigurationAdministration",
  REPORT_SETTINGS_ADMINISTRATION = "ReportSettingsAdministration",
  COMPANY_SETTINGS_ADMINISTRATION = "CompanySettingsAdministration",
  HY_DRY_DEVICE_SETTINGS_ADMINISTRATION = "HyDryDeviceSettingsAdministration",
  PROJECT_MANAGEMENT_SETTINGS_ADMINISTRATION = "ProjectManagementSettingsAdministration",
  ALL_DEVICES_SETTINGS_ADMINISTRATION = "AllDevicesSettingsAdministration",
  HY_DRY_MESSAGES_SETTINGS_ADMINISTRATION = "HyDryMessagesSettingsAdministration",
  IRES_CONFIGURATION = "IresConfiguration",
  FINISHED_PROJECTS_SETTINGS = "FinishedProjectsSettings",
  SYSTEM_SETTINGS = "SystemSettings",
  MAILBOX = "Mailbox",
  HY_DRY_DEVICE_SETTINGS = "HyDryDeviceSettings",
  DEVICE_IMPORT = "DeviceImport",
  DEVICE_EXPORT = "DeviceExport",
  DEVICE_SETTINGS = "DeviceSettings",
  DEVICE_CATEGORIES = "DeviceCategories",
  DEVICE_TEMPLATE_LIST = "DeviceTemplateList",
  DEVICE_IMPORT_HISTORY = "DeviceImportHistory",
  MESSAGE_SELECTION = "MessageSelection",
  OVERVIEW = "Overview",
  ARCHIVED_PROJECT_LIST = "ArchivedProjectList",
  PROJECT_LIST = "ProjectList",
  PROJECT_DETAILS = "ProjectDetails",
  REPORT_LIST = "ReportList",
  PROJECT_DEVICES = "ProjectDevices",
  IRES_DEVICE_LIST = "IresDeviceList",
  ALL_DEVICES = "AllDevices",
  DEVICE_HISTORY = "DeviceHistory",
  ALL_DEVICES_LIST = "AllDevicesList",
  DEVICE_TEMPLATE = "DeviceTemplate",
  DEVICES = "Devices",
  MEMBER_CLIENTS_MANAGEMENT = "MemberClientsManagement",
  BILLING_CATALOG_LIST = "BillingCatalogList",
  BILLING_CATALOG_DETAIL = "BillingCatalogDetail",
  INFO_CONFIG_LIST = "InfoConfigVersionList",
  INFO_CONFIG_VERSION = "InfoConfigVersionDetail",
  INFO_CONFIG_DRAFT = "BeschreibungDraftEditor",
  ADMIN_PORTAL = "AdminPortal",
  RENO_REVIEW_QUEUE = "RenoReviewQueue",
  RENO_REVIEW_STEPPER = "RenoReviewStepper",
  RENO_REVIEW_HIDDEN = "RenoReviewHidden",
  DEVICE_REPORT_LIST = "DeviceReportList",
  DEVICE_REPORT_RESULTS = "DeviceReportResults",
  DEVICE_REPORT_VIEWER = "DeviceReportViewer",
  CLIENT_ADMINISTRATION_LIST = "ClientAdministrationList",
  CLIENT_ADMINISTRATION_DETAIL = "ClientAdministrationDetail",
  CLIENT_ADMINISTRATION_SETUP = "ClientSetup",
  CLIENT_ADMINISTRATION_DEVICES = "ClientAdministrationDevices",
  CLIENT_ADMINISTRATION_SENSOR_DATA_EXPORT = "ClientSensorDataExport",
  CLIENT_ADMINISTRATION_PROJECTS = "ClientAdministrationProjects",
  CLIENT_ADMINISTRATION_REPORTS = "ClientAdministrationReports",
  CLIENT_ADMINISTRATION_USERS = "ClientAdministrationUsers",
  CLIENT_ADMINISTRATION_PDF_TEMPLATES = "ClientAdministrationPdfTemplates",
  CLIENT_ADMINISTRATION_PDF_TEMPLATE = "ClientAdministrationPdfTemplate",
  CLIENT_ADMINISTRATION_PARTNER = "ClientPartnerDetail",
  EXTERNAL_SYSTEM_LIST = "ExternalSystemList",
  ADMIN_COCKPIT = "AdminCockpit",
  MIRA_INSURER_LIST = "MiraInsurerList",
  MIRA_INSURER_REVIEW = "MiraInsurerReview",
  MIRA_COMPANY_LIST = "MiraCompanyList",
  MIRA_EXCEPTION_LIST = "MiraExceptionList",
}

const PROJECT_COMPONENTS: string[] = [
  ComponentName.ARCHIVED_PROJECT_LIST,
  ComponentName.PROJECT_LIST,
  ComponentName.PROJECT_DETAILS,
  ComponentName.PROJECT_DEVICES,
]

const ALL_DEVICES_COMPONENTS: string[] = [
  ComponentName.ALL_DEVICES,
  ComponentName.DEVICE_HISTORY,
  ComponentName.ALL_DEVICES_LIST,
  ComponentName.DEVICE_TEMPLATE,
  ComponentName.DEVICES,
]

const USER_SETTINGS_COMPONENTS: string[] = [
  ComponentName.USER_MANAGEMENT,
  ComponentName.USER_SETTINGS,
  ComponentName.VEHICLE_MANAGEMENT,
  ComponentName.ROLE_MANAGEMENT,
  ComponentName.PERMISSION_TABLE,
  ComponentName.VEHICLES,
  ComponentName.VEHICLE_DEVICES
]

const COMPANY_SETTINGS_COMPONENTS: string[] = [
  ComponentName.COMPANY_DATA,
  ComponentName.BRANCHES,
]

const REPORT_SETTINGS_COMPONENTS: string[] = [ComponentName.REPORT_SETTINGS]

const HY_DRY_DEVICE_COMPONENTS: string[] = [
  ComponentName.DRYING_HISTORY,
  ComponentName.IRES_DEVICE_DETAIL,
  ComponentName.IRES_DEVICE_LIST,
]

const OTHER_DEVICE_SETTINGS_COMPONENTS: string[] = [
  ComponentName.DEVICE_CATEGORIES,
  ComponentName.DEVICE_TEMPLATE_LIST,
  ComponentName.DEVICE_SETTINGS,
  ComponentName.DEVICE_IMPORT,
  ComponentName.DEVICE_EXPORT,
  ComponentName.DEVICE_IMPORT_HISTORY,
  ComponentName.PROJECT_DEVICES_SETTINGS,
]

const MESSAGE_COMPONENTS: string[] = [
  ComponentName.MESSAGE_LIST,
  ComponentName.MESSAGE_SELECTION,
  ComponentName.MAILBOX,
]

const PROJECT_MANAGEMENT_COMPONENTS: string[] = [
  ComponentName.PROJECT_SETTINGS,
  ComponentName.PROJECT_EDITING_SETTINGS,
  ComponentName.PROJECT_MANAGER_SETTINGS,
  ComponentName.DEVICE_MANAGER_SETTINGS,
]

const SYSTEM_SETTINGS_COMPONENTS: string[] = [
  ComponentName.SYSTEM_SETTINGS,
  ComponentName.SYSTEM_CONFIGURATION,
]

const TIME_TRACKING_HISTORY_COMPONENTS: string[] = [
  ComponentName.TIME_TRACKING_HISTORY,
]

const TIME_TRACKING_SETTINGS_COMPONENTS: string[] = [
  ComponentName.WORKING_HOURS,
  ComponentName.TIME_TRACKING_CONFIGURATIONS,
  ComponentName.DAILY_TRACKING_SETTINGS,
]

const SCHEDULING_SETTINGS_COMPONENTS: string[] = [
  ComponentName.SCHEDULING_SETTINGS,
]

const BILLING_CATALOG_COMPONENTS: string[] = [
  ComponentName.BILLING_CATALOG_LIST,
  ComponentName.BILLING_CATALOG_DETAIL,
]

/**
 * IRES admin-portal routes. Access is resolved asynchronously in `router.beforeEach`
 * (`isOfficeAdminClient()` reads Firestore), so `hasAccess()` deliberately allows these and the
 * real gate lives in the router. Exported for that reason.
 */
export const ADMIN_PORTAL_COMPONENTS: string[] = [
  ComponentName.ADMIN_PORTAL,
  ComponentName.RENO_REVIEW_QUEUE,
  ComponentName.RENO_REVIEW_STEPPER,
  ComponentName.RENO_REVIEW_HIDDEN,
  ComponentName.DEVICE_REPORT_LIST,
  ComponentName.DEVICE_REPORT_RESULTS,
  ComponentName.DEVICE_REPORT_VIEWER,
  ComponentName.CLIENT_ADMINISTRATION_LIST,
  ComponentName.CLIENT_ADMINISTRATION_DETAIL,
  ComponentName.CLIENT_ADMINISTRATION_SETUP,
  ComponentName.CLIENT_ADMINISTRATION_DEVICES,
  ComponentName.CLIENT_ADMINISTRATION_SENSOR_DATA_EXPORT,
  ComponentName.CLIENT_ADMINISTRATION_PROJECTS,
  ComponentName.CLIENT_ADMINISTRATION_REPORTS,
  ComponentName.CLIENT_ADMINISTRATION_USERS,
  ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES,
  ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATE,
  ComponentName.CLIENT_ADMINISTRATION_PARTNER,
  ComponentName.EXTERNAL_SYSTEM_LIST,
  ComponentName.INFO_CONFIG_LIST,
  ComponentName.INFO_CONFIG_VERSION,
  ComponentName.INFO_CONFIG_DRAFT,
  ComponentName.MIRA_INSURER_LIST,
  ComponentName.MIRA_INSURER_REVIEW,
  ComponentName.MIRA_COMPANY_LIST,
  ComponentName.MIRA_EXCEPTION_LIST,
  // Deliberately not in MANAGE_CLIENTS_COMPONENTS: it is the portal client's landing page, so a
  // portal user without that capability must still reach it (and sees the empty state).
  ComponentName.ADMIN_COCKPIT,
]

/** Not open while "Sicht auf" is active (ticket 2610051): action-only pages and the IRES Admin Client. */
export const VIEW_AS_BLOCKED_COMPONENTS: string[] = [
  ComponentName.DEVICE_IMPORT,
  ComponentName.PROJECT_EDITING_SETTINGS,
  ...ADMIN_PORTAL_COMPONENTS,
];

/**
 * Admin-portal routes that additionally need the `RATE_DRYINGS` capability.
 *
 * Separate from `ADMIN_PORTAL_COMPONENTS` because portal membership must not imply the Drying Expert
 * role (D24/D31): the admin client is `clientType: "IRES"`, so every user on it would otherwise
 * qualify. The portal landing page itself is deliberately absent — any portal user may see it, and it
 * hides the tiles they cannot use.
 *
 * Checked in `router.beforeEach` rather than in the components: the capability is role-resolved, and
 * role settings load asynchronously, so a component-level check on a cold page load reads "denied"
 * and bounced the user out on refresh. The guard can await `whenCapabilitiesAreKnown()`.
 */
export const RATE_DRYINGS_COMPONENTS: string[] = [
  ComponentName.RENO_REVIEW_QUEUE,
  ComponentName.RENO_REVIEW_STEPPER,
  ComponentName.RENO_REVIEW_HIDDEN,
]

/**
 * Admin-portal routes that additionally need the `MANAGE_PRODUCTION` capability.
 *
 * Same shape and the same reasoning as `RATE_DRYINGS_COMPONENTS`: portal membership must not imply
 * production access, and the check belongs in `router.beforeEach` rather than in a component,
 * because the capability is role-resolved and role settings load asynchronously — a component-level
 * check reads "denied" on a cold page load and bounces the user out on every refresh.
 */
export const MANAGE_PRODUCTION_COMPONENTS: string[] = [
  ComponentName.DEVICE_REPORT_LIST,
  ComponentName.DEVICE_REPORT_RESULTS,
  ComponentName.DEVICE_REPORT_VIEWER,
]

/**
 * Admin-portal routes that additionally need the `MANAGE_CLIENTS` capability.
 *
 * Same shape and reasoning as `MANAGE_PRODUCTION_COMPONENTS`. This guard only decides what the
 * browser shows: the data behind these routes comes from Cloud callables that repeat the portal and
 * capability checks server-side (`listClientsForAdministration`), which is the actual boundary.
 */
export const MANAGE_CLIENTS_COMPONENTS: string[] = [
  ComponentName.CLIENT_ADMINISTRATION_LIST,
  ComponentName.CLIENT_ADMINISTRATION_DETAIL,
  ComponentName.CLIENT_ADMINISTRATION_SETUP,
  ComponentName.CLIENT_ADMINISTRATION_DEVICES,
  // The Sensordaten-Export moved here from IRES Office's settings (#2610088).
  ComponentName.CLIENT_ADMINISTRATION_SENSOR_DATA_EXPORT,
  ComponentName.CLIENT_ADMINISTRATION_PROJECTS,
  ComponentName.CLIENT_ADMINISTRATION_REPORTS,
  ComponentName.CLIENT_ADMINISTRATION_USERS,
  ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES,
  ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATE,
  ComponentName.CLIENT_ADMINISTRATION_PARTNER,
  ComponentName.EXTERNAL_SYSTEM_LIST,
  ComponentName.INFO_CONFIG_LIST,
  ComponentName.INFO_CONFIG_VERSION,
  ComponentName.INFO_CONFIG_DRAFT,
  // Mira's insurer list and its review (Track #2610040 P3): the same capability, decided 2026-10-14.
  ComponentName.MIRA_INSURER_LIST,
  ComponentName.MIRA_INSURER_REVIEW,
  // The companies of Mira's market comparison (#2610009 C4): the same capability as the insurer list.
  ComponentName.MIRA_COMPANY_LIST,
  // Mira-Ausnahmen (Track #2610040 R8): the same capability.
  ComponentName.MIRA_EXCEPTION_LIST,
]

const ADMINISTRATION_SETTINGS_COMPONENTS: string[] = [
  ComponentName.USER_SETTINGS_ADMINISTRATION,
  ComponentName.ROLE_SETTINGS_ADMINISTRATION,
  ComponentName.SYSTEM_CONFIGURATION_ADMINISTRATION,
  ComponentName.REPORT_SETTINGS_ADMINISTRATION,
  ComponentName.COMPANY_SETTINGS_ADMINISTRATION,
  ComponentName.HY_DRY_DEVICE_SETTINGS_ADMINISTRATION,
  ComponentName.PROJECT_MANAGEMENT_SETTINGS_ADMINISTRATION,
  ComponentName.MEMBER_CLIENTS_MANAGEMENT
]

const MEMBER_CLIENTS_MANAGEMENT_COMPONENTS: string[] = [
  ComponentName.MEMBER_CLIENTS_MANAGEMENT,
]

/** The Mira landing page and every Mira screen below it, all behind {@link miraAccess}. */
const MIRA_COMPONENTS: string[] = [
  ComponentName.MIRA,
  ComponentName.MIRA_DATA_QUALITY,
  ComponentName.MIRA_DRYING,
  ComponentName.MIRA_DEVICES,
  ComponentName.MIRA_BUSINESS_DEVELOPMENT,
  ComponentName.MIRA_PROCESS_FLOW,
  ComponentName.MIRA_INSURANCE,
  ComponentName.MIRA_UNLOCK,
]

/**
 * Components an IRES admin-portal client must not reach, by navigation or by direct URL.
 *
 * Mirrors what `Settings.hidesClientBusinessSettings` hides and what `Scaffold` drops from the
 * navigation, as required by `src/components/settings/CLAUDE.md`: anything hidden in Settings has
 * to be redirected here too, or it stays reachable by typing the URL.
 *
 * The admin portal runs no projects and bills nobody, so these lead to empty or meaningless pages.
 *
 * Note the vehicle components are listed individually rather than via `USER_SETTINGS_COMPONENTS`:
 * that group also contains role management and the permission table, which the admin portal very
 * much needs, since that is where the Drying Expert role is configured.
 */
const ADMIN_PORTAL_HIDDEN_COMPONENTS: string[] = [
  ...PROJECT_COMPONENTS,
  ...PROJECT_MANAGEMENT_COMPONENTS,
  ...COMPANY_SETTINGS_COMPONENTS,
  ...REPORT_SETTINGS_COMPONENTS,
  ...TIME_TRACKING_HISTORY_COMPONENTS,
  ...TIME_TRACKING_SETTINGS_COMPONENTS,
  ...SCHEDULING_SETTINGS_COMPONENTS,
  ...BILLING_CATALOG_COMPONENTS,
  ...MEMBER_CLIENTS_MANAGEMENT_COMPONENTS,

  ComponentName.VEHICLE_MANAGEMENT,
  ComponentName.VEHICLES,
  ComponentName.VEHICLE_DEVICES,

  ComponentName.FINISHED_PROJECTS_SETTINGS,
  ComponentName.MAP,
  ...MIRA_COMPONENTS,

  // HyDry Geräte and Nachrichten, hidden from the navigation too (Taimoor, 2026-09-30). The device
  // pages below the list go with it. Not MESSAGE_COMPONENTS as a whole: MESSAGE_SELECTION and MAILBOX
  // are pages of Settings, which the portal keeps.
  ...HY_DRY_DEVICE_COMPONENTS,
  ComponentName.RATE_DRYING,
  ComponentName.SAMPLES,
  ComponentName.ALL_MESSAGES,
  ComponentName.MESSAGE_LIST,
]

const IRES_SETTINGS_COMPONENTS: string[] = [
  ComponentName.IRES_CONFIGURATION
]

const HY_DRY_DEVICE_SETTINGS_COMPONENTS: string[] = [
  ComponentName.HY_DRY_DEVICE_SETTINGS,
]

/** Checks whether the active user's client can manage projects. */
function areProjectComponentsAccessible(activeUser: User | null) {
  return manageProjectsClientAccess(activeUser);
}

/**
 * Checks whether user settings components are accessible,
 * including sub-checks for individual settings pages.
 */
function areUserSettingsComponentsAccessible(
  activeUser: User | null,
  targetComponentName: string
) {
  if (!userSettingsAccess(activeUser)) {
    return false;
  }

  if (
    targetComponentName === ComponentName.USER_SETTINGS &&
    !usersConfigurationAccess(activeUser)
  ) {
    return false;
  } else if (
    targetComponentName === ComponentName.VEHICLE_MANAGEMENT &&
    !vehicleManagementAccess(activeUser)
  ) {
    return false;
  } else if (
    (targetComponentName === ComponentName.ROLE_MANAGEMENT ||
      targetComponentName === ComponentName.PERMISSION_TABLE) &&
    !roleManagementAccess(activeUser)
  ) {
    return false;
  }

  return true;
}

/**
 * Checks whether device settings components (categories, templates, imports)
 * are accessible, with sub-checks per component. Mirrors the "Alle Geräte"
 * section of `Settings.vue` item by item, so a page hidden there cannot be
 * opened by its URL either.
 */
function areOtherDeviceSettingsComponentsAccessible(
  activeUser: User | null,
  targetComponentName: string
) {
  if (
    !allDevicesSettingsClientAccess() ||
    (!otherDeviceSettingsAccess(activeUser) && !defineOwnAllDevicesSettingsClientAccess())
  ) {
    return false;
  }

  if (
    (targetComponentName === ComponentName.DEVICE_CATEGORIES ||
      targetComponentName === ComponentName.DEVICE_TEMPLATE_LIST ||
      targetComponentName === ComponentName.PROJECT_DEVICES_SETTINGS) &&
    !deviceSettingsItemAccess(activeUser)
  ) {
    return false;
  }

  if ((
    targetComponentName === ComponentName.DEVICE_SETTINGS ||
    targetComponentName === ComponentName.DEVICE_IMPORT ||
    targetComponentName === ComponentName.DEVICE_IMPORT_HISTORY ||
    targetComponentName === ComponentName.DEVICE_EXPORT  
  ) && !importDevicesAccess(activeUser)) {
    return false;
  }

  return true;
}

function areMessageComponentsAccessible(
  activeUser: User | null,
  targetComponentName: string
) {
  if (!manageMessagesClientAccess() && !manageHyDryMessagesSettingsClientAccess()) {
    return false;
  }

  if (
    !messageSettingsAccess(activeUser) &&
    targetComponentName !== ComponentName.MESSAGE_LIST
  ) {
    return false;
  }

  if (
    targetComponentName === ComponentName.MESSAGE_SELECTION &&
    !messageSelectionAccess(activeUser)
  ) {
    return false;
  }

  if (
    targetComponentName === ComponentName.MAILBOX &&
    !mailboxAccess(activeUser)
  ) {
    return false;
  }

  return true;
}

/**
 * Checks whether system settings components are accessible,
 * with a sub-check for the SystemConfiguration page.
 */
function areSystemSettingsComponentsAccessible(
  activeUser: User | null,
  targetComponentName: string
) {
  if (!systemSettingsSectionAccess(activeUser)) {
    return false;
  }

  if (
    targetComponentName === ComponentName.SYSTEM_CONFIGURATION &&
    !systemConfigurationAccess(activeUser)
  ) {
    return false;
  }

  return true;
}

/**
 * Checks whether administration settings components are accessible,
 * with a sub-check for the SystemConfiguration distribution page.
 */
function areAdministrationSettingsComponentsAccessible(
  activeUser: User | null,
  targetComponentName: string
) {
  if (!viewAdminsitrationSettingsAccess(activeUser)) {
    return false;
  }

  if (
    targetComponentName === ComponentName.SYSTEM_CONFIGURATION_ADMINISTRATION &&
    !viewSystemConfigurationDistributionAccess(activeUser)
  ) {
    return false;
  }

  return true;
}

/**
 * Determines whether the current user is allowed to navigate to the given route component.
 *
 * Called by the Vue Router `beforeEach` guard. Returns `true` if access is allowed,
 * `false` to block navigation and redirect. Checks are grouped by feature domain:
 * projects, devices, messages, and settings sub-sections.
 *
 * @param targetComponentName - The `name` property of the target route component.
 */
export function hasAccess(targetComponentName: string | undefined) {
  // Not `UserModule.activeUser`: until the first users snapshot after a page refresh it answers
  // the built-in Master account, which passed every route (ticket 2610049). This resolves the
  // signed-in user from the session, and is Master only for a session that really has no user.
  // While "Sicht auf" is active it is the chosen user: pages open as they would for them (2610052).
  const activeUser = getEffectiveUserLazily();

  if (!targetComponentName || activeUser?.id === "Master") {
    return true;
  }

  // "Sicht auf" is read-only (ticket 2610051): pages that exist only to change something, and the
  // IRES Admin Client as a whole, are not open while another user is shown.
  if (isViewAsSelected() && VIEW_AS_BLOCKED_COMPONENTS.includes(targetComponentName)) {
    return false;
  }

  // Portal shape for the IRES admin portal. Reads the value cached by
  // resolveOfficeAdminPortalClient(), so it stays synchronous like the rest of this function.
  if (
    isOfficeAdminPortalClient() &&
    ADMIN_PORTAL_HIDDEN_COMPONENTS.includes(targetComponentName)
  ) {
    return false;
  }

  if (PROJECT_COMPONENTS.includes(targetComponentName)) {
    if (!areProjectComponentsAccessible(activeUser)) {
      return false;
    }
  }

  if (HY_DRY_DEVICE_COMPONENTS.includes(targetComponentName)) {
    if (!manageHyDryDevicesClientAccess()) {
      return false;
    }
  }

  if (ALL_DEVICES_COMPONENTS.includes(targetComponentName)) {
    if (!manageAllDevicesClientAccess()) {
      return false;
    }
  }

  if (MESSAGE_COMPONENTS.includes(targetComponentName)) {
    if (
      !areMessageComponentsAccessible(activeUser, targetComponentName)
    ) {
      return false;
    }
  }
  
  if (USER_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!areUserSettingsComponentsAccessible(activeUser, targetComponentName)) {
      return false;
    }
  } else if (COMPANY_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!companySettingsAccess(activeUser)) {
      return false;
    }
  } else if (REPORT_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!reportSettingsAccess(activeUser)) {
      return false;
    }
  } else if (OTHER_DEVICE_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (
      !areOtherDeviceSettingsComponentsAccessible(
        activeUser,
        targetComponentName
      )
    ) {
      return false;
    }
  } else if (PROJECT_MANAGEMENT_COMPONENTS.includes(targetComponentName)) {
    if (
      !projectSettingsAccess(activeUser) &&
      !defineProjectManagementSettingsForGroupClientAccess()
    ) {
      return false;
    }
  } else if (SYSTEM_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (
      !areSystemSettingsComponentsAccessible(activeUser, targetComponentName)
    ) {
      return false;
    }
  } else if (TIME_TRACKING_HISTORY_COMPONENTS.includes(targetComponentName)) {
    if (!canAccessTimeTrackingHistory(
      manageTimeTrackingClientAccess(),
      alwaysAllowedToViewTimeTrackingAccess(activeUser)
    )) {
      return false;
    }
  } else if (TIME_TRACKING_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!timeTrackingSettingsAccess(activeUser)) {
      return false;
    }
  } else if (SCHEDULING_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!schedulingSettingsAccess(activeUser)) {
      return false;
    }
  } else if (BILLING_CATALOG_COMPONENTS.includes(targetComponentName)) {
    if (!billingCatalogSettingsAccess(activeUser)) {
      return false;
    }
  } else if (ADMIN_PORTAL_COMPONENTS.includes(targetComponentName)) {
    // Access is checked async in router.beforeEach — always allow here
    return true;
  } else if (IRES_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!iresSettingsAccess(activeUser)) {
      return false;
    }
  } else if (ADMINISTRATION_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (
      !areAdministrationSettingsComponentsAccessible(
        activeUser, 
        targetComponentName
      )
    ) {
      return false;
    }
  } else if (MEMBER_CLIENTS_MANAGEMENT_COMPONENTS.includes(targetComponentName)) {
    if (!createMemberClientAccess(activeUser)) {
      return false;
    }
  } else if (HY_DRY_DEVICE_SETTINGS_COMPONENTS.includes(targetComponentName)) {
    if (!hyDryDeviceSettingsAccess(activeUser)) {
      return false;
    }
  } else if (targetComponentName === ComponentName.MAP) {
    // The standalone Karte screen. It had no guard at all until 2026-09-07: `ComponentName.MAP`
    // appeared only in `ADMIN_PORTAL_HIDDEN_COMPONENTS`, so `hasAccess("Map")` answered true for
    // every ordinary client and the screen was reachable by typing the URL, whatever the
    // navigation showed. Hiding the sidebar entry is necessary and not sufficient; this is the
    // other half.
    if (!mapPageAccess(activeUser)) {
      return false;
    }
  } else if (MIRA_COMPONENTS.includes(targetComponentName)) {
    // Guarded here as well as hidden in the navigation, for the reason the Karte branch above gives.
    if (!miraAccess(activeUser)) {
      return false;
    }
  }

  return true;
}

/** The "Neues Projekt" routes. They share `ComponentName.PROJECT_DETAILS` with an existing project. */
export const CREATE_PROJECT_PATHS = ["/main/createProject", "/main/cockpit/createProject"];


/**
 * Path-level checks for routes that {@link hasAccess} cannot tell apart by component name.
 *
 * "Neues Projekt" opens the project detail component, so `hasAccess` sees it as viewing a project
 * and the right to create one was only enforced on the buttons that lead there: typing the address
 * opened the form for anyone (ticket 2610049). This applies the same `createProjectAccess` the
 * buttons use.
 *
 * @param path - The target route's `path`.
 * @param projectSettings - The client's project settings; the router loads them before calling.
 */
export function hasPathAccess(path: string, projectSettings: ProjectSettingsV2) {
  if (!CREATE_PROJECT_PATHS.includes(path)) {
    return true;
  }

  const activeUser = getEffectiveUserLazily();

  return activeUser?.id === "Master" || createProjectAccess(activeUser, projectSettings);
}
